/**
 * 🏋️🍳 InteractiveSpaceShell — a simple teaching-page wrapper for the
 * Interactive Gym and Interactive Kitchen.
 *
 * Feb 2026 architectural pivot (owner directive):
 *   "The AR is NOT what I had in mind... they are using and teaching
 *    what was inputed by the user... this part is NOT like Pokémon GO.
 *    Its a teaching tool and independent helper... the AR would be
 *    great for other areas but not the kitchen or gym unless its
 *    capturing equipment or items for the avatar to use with the user.
 *    The camera 📷 is meant to add what the user has to mimic it in
 *    the app."
 *
 * So this shell is NOT a live-camera Pokémon-Go view anymore. The page
 * is a normal, focused teaching surface. The camera lives inside the
 * scanner panels as a transient <CameraCapture /> modal — only opened
 * when the user actually wants to add gear / food / a room snapshot.
 *
 * Kept the export name `InteractiveARShell` for zero-diff import sites,
 * but the semantics are now "SpaceShell". The old exports (children,
 * onCameraReady, fallbackGradient) are still accepted so upstream
 * callers don't break.
 */
import { AvatarQuickLaunch } from "./AvatarQuickLaunch";
import { PantryScannerPanel } from "./PantryScannerPanel";
import { EquipmentScannerPanel } from "./EquipmentScannerPanel";
import { NutrientChart } from "./NutrientChart";
import { RecipeMatchPanel } from "./RecipeMatchPanel";
import { ARFirstUseTour } from "./ARFirstUseTour";
import { ChefHat, Dumbbell, Camera, BookOpen } from "lucide-react";

export function InteractiveARShell({ space, children, fallbackGradient }) {
  const isKitchen = space === "kitchen";
  const spaceLabel = isKitchen ? "kitchen" : "gym";
  const SpaceIcon = isKitchen ? ChefHat : Dumbbell;
  const accent = isKitchen ? "amber" : "sky";
  const bg = fallbackGradient || (isKitchen
    ? "bg-gradient-to-br from-amber-950/40 via-black to-neutral-950"
    : "bg-gradient-to-br from-sky-950/40 via-black to-neutral-950");

  return (
    <div className={`relative w-full min-h-screen ${bg}`} data-testid={`space-shell-${space}`}>
      {/* Optional first-use tour (opt-in via existing seed) */}
      <ARFirstUseTour space={space} />

      <div className="max-w-6xl mx-auto px-4 pt-28 pb-16 space-y-6">
        {/* Hero */}
        <header className="space-y-2" data-testid={`space-hero-${space}`}>
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${
            isKitchen ? "border-amber-400/40 bg-amber-500/10 text-amber-200" : "border-sky-400/40 bg-sky-500/10 text-sky-200"
          } text-[10px] uppercase tracking-widest font-bold`}>
            <SpaceIcon size={12} />
            Interactive {spaceLabel}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
            {isKitchen ? "Cook alongside your avatar." : "Train alongside your avatar."}
          </h1>
          <p className="text-white/70 text-sm max-w-2xl">
            {isKitchen
              ? "A teaching space that uses YOUR pantry and YOUR kitchen. Snap items with the camera to add them, then your avatar plans and walks you through meals inside the app."
              : "A teaching space that uses YOUR gear and YOUR room. Snap items with the camera to add them, then your avatar plans and walks you through sessions inside the app."}
          </p>

          {/* Three-step explainer — peer-tone, addresses "I don't know what this is for" */}
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2" data-testid={`space-explainer-${space}`}>
            <div className="rounded-lg border border-white/10 bg-black/40 p-3">
              <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${
                isKitchen ? "text-amber-300" : "text-sky-300"
              }`}>
                <Camera size={11} /> 1 · Snap
              </div>
              <div className="text-sm text-white/85 mt-1">
                {isKitchen ? "Snap the fridge, cupboard, appliances." : "Snap your gear — dumbbells, machines, bands."}
              </div>
              <div className="text-[10px] text-white/50 mt-0.5">
                Camera opens on tap, closes after the shot.
              </div>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/40 p-3">
              <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${
                isKitchen ? "text-amber-300" : "text-sky-300"
              }`}>
                <SpaceIcon size={11} /> 2 · Review
              </div>
              <div className="text-sm text-white/85 mt-1">
                Confirm what got detected. Edit, remove, or add manually.
              </div>
              <div className="text-[10px] text-white/50 mt-0.5">
                Nothing is saved until you approve it.
              </div>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/40 p-3">
              <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${
                isKitchen ? "text-amber-300" : "text-sky-300"
              }`}>
                <BookOpen size={11} /> 3 · Learn
              </div>
              <div className="text-sm text-white/85 mt-1">
                Your avatar teaches sessions using ONLY what you have.
              </div>
              <div className="text-[10px] text-white/50 mt-0.5">
                Inside the app — no live camera during teaching.
              </div>
            </div>
          </div>
        </header>

        {/* 🧍 Avatar quick-launch */}
        <AvatarQuickLaunch space={space} />

        {/* 🥫 Batch scanner + inventory review */}
        <section
          className="rounded-2xl border border-white/10 bg-black/40 p-2"
          data-testid={`pantry-section-${space}`}
        >
          <PantryScannerPanel space={space} />
        </section>

        {/* 🍳 Recipe Match (kitchen only) — cook from your pantry */}
        {isKitchen && (
          <section data-testid="recipe-match-section">
            <RecipeMatchPanel />
          </section>
        )}

        {/* 📸 Single-item scanner + session builder */}
        <section
          className="rounded-2xl border border-white/10 bg-black/40 p-2"
          data-testid={`equipment-section-${space}`}
        >
          <EquipmentScannerPanel space={space} />
        </section>

        {/* 🥗 Nutrition (kitchen only) */}
        {isKitchen && (
          <section className="rounded-2xl overflow-hidden" data-testid="kitchen-nutrition-section">
            <NutrientChart />
          </section>
        )}

        {/* Page-specific extras from caller */}
        {children}
      </div>
    </div>
  );
}

export default InteractiveARShell;
